import { Button, CloseButton, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Currency,
  type SubscriptionPlanConfig,
  type SubscriptionResponse,
  SubscriptionStatus,
  type UsageResponse,
} from '@/api';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { useUndoToast } from '@/components/Ui/Toast';
import { scrollToSection } from '@/components/Ui/UiLink';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { formatCredit, formatEur, formatPercent, vfiatToEur } from '@/lib/money';
import { shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { errorMessage } from '../format';
import classes from './Billing.module.css';
import { planLabel } from './billing-labels';
import { HistorySection } from './HistorySection';
import { PlansSection } from './PlansSection';
import { UsageSection } from './UsageSection';

const PRESETS = [10, 15, 50] as const;

function isActive(sub: SubscriptionResponse | null | undefined) {
  return sub?.status === SubscriptionStatus.ACTIVE || sub?.status === SubscriptionStatus.PastDue;
}

export function BillingScreen() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const toast = useUndoToast();
  const api = useRustyState.getState().api;
  const session = useRustyState((s) => s.api?.auth?.getSession());
  const balance = useRustyState((s) => s.accountBalance);

  const [subscription, setSubscription] = useState<SubscriptionResponse | null | undefined>(
    undefined
  );
  const [plans, setPlans] = useState<SubscriptionPlanConfig[] | null>(null);
  const [usage, setUsage] = useState<UsageResponse[] | null>(null);
  const [preset, setPreset] = useState<number | null>(15);
  const [other, setOther] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const load = useCallback(async () => {
    const [sub, planList, usageRows] = await Promise.allSettled([
      api.getCurrentSubscription({ onError: false }),
      api.getSubscriptionPlans(),
      api.getSubscriptionUsage({ onError: false }),
    ]);
    setSubscription(sub.status === 'fulfilled' ? sub.value : null);
    setPlans(planList.status === 'fulfilled' ? planList.value : []);
    setUsage(usageRows.status === 'fulfilled' ? usageRows.value : null);
    void useRustyState.getState().refreshAccountBalance();
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const section = params.get('section');
  useScrollToSection(section === 'plans' ? section : null, location.key);

  const deposit = params.get('deposit');
  const checkout = params.get('checkout');
  const notice =
    deposit === 'success'
      ? t('billing.notice.depositSuccess')
      : deposit === 'cancel'
        ? t('billing.notice.depositCancel')
        : checkout === 'success'
          ? t('billing.notice.checkoutSuccess')
          : checkout === 'cancel'
            ? t('billing.notice.checkoutCancel')
            : null;
  const dismissNotice = () => {
    const next = new URLSearchParams(params);
    next.delete('deposit');
    next.delete('checkout');
    setParams(next, { replace: true });
  };

  const currentPlan =
    plans?.find((p) => p.id === subscription?.plan_id || p.name === subscription?.plan_name) ??
    null;
  const isFree = currentPlan?.is_free_plan ?? subscription?.is_free_plan ?? true;
  const nextPlan =
    subscription?.next_plan_id && subscription.next_plan_id !== subscription.plan_id
      ? (plans?.find((p) => p.id === subscription.next_plan_id) ?? null)
      : null;

  const usedPct = (() => {
    const ratios = (usage ?? []).filter((u) => u.limit > 0).map((u) => (u.used / u.limit) * 100);
    return ratios.length ? Math.min(100, Math.round(Math.max(...ratios))) : null;
  })();

  const eur = balance?.find(([c]) => c === Currency.EUR);
  const credit = eur ? formatCredit(eur[1], i18n.language) : '–';

  const otherAmount = Number(other.replace(',', '.'));
  const amount =
    preset ??
    (Number.isFinite(otherAmount) && otherAmount >= 1 && otherAmount <= 10000 ? otherAmount : null);

  const pay = async () => {
    if (!amount) return;
    setPaying(true);
    setPayError(null);
    try {
      const res = await api.newDeposit(
        {
          amount: Math.round(amount * 100),
          currency: Currency.EUR,
        },
        { onSuccess: false, onError: false }
      );
      if (res?.type === 'STRIPE') {
        window.location.href = res.content.checkout_url;
        return;
      }
      setPayError(t('billing.credit.payFailed'));
    } catch (e) {
      setPayError(errorMessage(e) ?? t('billing.credit.payFailed'));
    }
    setPaying(false);
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await api.createCustomerPortalSession({ onSuccess: false });
      window.location.href = res.portal_url;
    } catch {
      setPortalBusy(false);
    }
  };

  const switchPlan = async (plan: SubscriptionPlanConfig) => {
    const doSwitch = async () => {
      try {
        const res = await api.subscribeToplan(plan.id, { onSuccess: false, onError: false });
        if (res.type === 'Checkout') {
          window.location.href = res.content.redirect_url;
          return;
        }
        toast(t('billing.plans.switched', { name: planLabel(plan.name) }));
        await load();
      } catch (e) {
        const data = (e as { response?: { status?: number; data?: Record<string, unknown> } })
          ?.response;
        if (data?.status === 400 && data.data?.error === 'CustomerPortalRequired') {
          await openPortal();
          return;
        }
        toast(errorMessage(e) ?? t('billing.plans.switchFailed'));
      }
    };
    const current = currentPlan?.max_retention_days ?? null;
    const target = plan.max_retention_days ?? null;
    if (current !== null && target !== null && target < current) {
      modals.openConfirmModal({
        title: t('billing.plans.downgradeTitle', { name: planLabel(plan.name) }),
        children: <p style={{ margin: 0 }}>{t('billing.plans.downgradeBody', { days: target })}</p>,
        labels: { confirm: t('billing.plans.downgradeConfirm'), cancel: t('ui.cancel') },
        onConfirm: () => void doSwitch(),
      });
      return;
    }
    await doSwitch();
  };

  const locale = i18n.language;

  return (
    <Page>
      <PageHeader title={t('billing.pageTitle')} />

      {notice && (
        <div className={shared.okBanner} role="status">
          <div className={shared.row} style={{ justifyContent: 'space-between' }}>
            <span>{notice}</span>
            <CloseButton aria-label={t('ui.close')} onClick={dismissNotice} />
          </div>
        </div>
      )}

      <div className={classes.cards}>
        <section className={classes.card} aria-labelledby="billing-plan-h">
          <h2 id="billing-plan-h" className={classes.cardTitle}>
            {t('billing.plan.title')}
          </h2>
          <div className={classes.headline}>
            <span className={classes.big}>
              {subscription === undefined
                ? t('ui.loading')
                : currentPlan
                  ? planLabel(currentPlan.name)
                  : subscription
                    ? planLabel(subscription.plan_name)
                    : t('billing.plan.none')}
            </span>
            {currentPlan && subscription && (
              <span className={shared.sub}>
                {currentPlan.price > 0
                  ? t('billing.plan.paidLine', {
                      price: formatEur(vfiatToEur(currentPlan.price), i18n.language, 0, 2),
                      date: shortDate(subscription.current_period_end, locale),
                    })
                  : t('billing.plan.freeLine', {
                      date: shortDate(subscription.current_period_end, locale),
                    })}
              </span>
            )}
          </div>
          {subscription?.status === SubscriptionStatus.PastDue && (
            <span className={shared.riskText}>{t('billing.plan.pastDue')}</span>
          )}
          {subscription && !isActive(subscription) && (
            <span className={shared.riskText}>
              {t('billing.plan.inactive', { status: subscription.status })}
            </span>
          )}
          {nextPlan && subscription && (
            <span className={shared.help}>
              {t('billing.plan.next', {
                name: planLabel(nextPlan.name),
                date: shortDate(subscription.current_period_end, locale),
              })}
            </span>
          )}
          {usedPct !== null && (
            <>
              <div
                className={classes.bar}
                role="img"
                aria-label={t('billing.plan.usedAria', { pct: usedPct })}
              >
                <div className={classes.barFill} style={{ width: `${usedPct}%` }} />
              </div>
              <span className={shared.sub}>
                {t('billing.plan.used', { pct: formatPercent(usedPct, locale) })}
              </span>
            </>
          )}
          <div className={shared.row} style={{ marginTop: 4 }}>
            <Button size="xs" variant="default" onClick={() => scrollToSection('plans')}>
              {t('billing.plan.change')}
            </Button>
            {!isFree && (
              <Button size="xs" variant="default" onClick={openPortal} loading={portalBusy}>
                {t('billing.plan.portal')}
              </Button>
            )}
          </div>
        </section>

        <section className={classes.card} aria-labelledby="billing-credit-h">
          <h2 id="billing-credit-h" className={classes.cardTitle}>
            {t('billing.credit.title')}
          </h2>
          <span className={classes.big}>{credit}</span>
          <span id="billing-amount-l" className={shared.sub}>
            {t('billing.credit.add')}
          </span>
          <fieldset
            className={classes.amounts}
            aria-labelledby="billing-amount-l"
            style={{ border: 0, padding: 0, margin: 0 }}
          >
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={classes.amount}
                aria-pressed={preset === p}
                onClick={() => {
                  setPreset(p);
                  setOther('');
                }}
              >
                {formatEur(p, i18n.language, 0, 0)}
              </button>
            ))}
            <TextInput
              aria-label={t('billing.credit.otherLabel')}
              placeholder={t('billing.credit.otherPlaceholder')}
              inputMode="decimal"
              w={110}
              value={other}
              onChange={(e) => {
                setOther(e.currentTarget.value);
                setPreset(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && amount && !paying) void pay();
              }}
            />
            <Button onClick={pay} loading={paying} disabled={!amount}>
              {amount
                ? t('billing.credit.pay', { amount: formatEur(amount, i18n.language, 0, 2) })
                : t('billing.credit.payEmpty')}
            </Button>
          </fieldset>
          {payError && (
            <p className={shared.errorText} role="alert">
              {payError}
            </p>
          )}
          <span className={shared.help}>
            {t('billing.credit.otherWays')}{' '}
            <a href="mailto:support@formshive.com">support@formshive.com</a>
          </span>
        </section>
      </div>

      <section className={shared.panel} aria-labelledby="billing-runout-h">
        <strong id="billing-runout-h">{t('billing.runOut.title')}</strong>
        <span className={shared.sub}>
          {session?.email
            ? t('billing.runOut.body', { email: session.email })
            : t('billing.runOut.bodyNoEmail')}
        </span>
      </section>

      <UsageSection usage={usage} subscription={subscription ?? null} />

      <PlansSection
        plans={plans}
        subscription={subscription ?? null}
        currentPlanId={currentPlan?.id ?? null}
        onSwitch={switchPlan}
      />

      <HistorySection />
    </Page>
  );
}
